import { useState } from "react";
import {
  Box,
  Typography,
  Rating,
  TextField,
  Button,
  Stack,
  useTheme,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SaveIcon from "@mui/icons-material/Save";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { tokens } from "../../../theme";
import { ApiEvaluation } from "../StreamFundApplication";

interface StageEvaluationCardProps {
  criteria: Record<string, string>;
  evaluation: ApiEvaluation | null;
  canEdit: boolean;
  locked?: boolean;
  lockedReason?: string;
  onSave: (payload: {
    scores: Record<string, number>;
    comments: string;
    risks: string;
  }) => Promise<ApiEvaluation | undefined>;
}

export default function StageEvaluationCard({
  criteria,
  evaluation,
  canEdit,
  locked = false,
  lockedReason,
  onSave,
}: StageEvaluationCardProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const mutedCard = colors.primary[800];
  const textPrimary = colors.primary[700];
  const textMuted = colors.primary[300];

  const [scores, setScores] = useState<Record<string, number>>(
    evaluation?.scores ?? {},
  );
  const [comments, setComments] = useState(evaluation?.comments ?? "");
  const [risks, setRisks] = useState(evaluation?.risks ?? "");
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [isComplete, setIsComplete] = useState(
    evaluation?.is_complete ?? false,
  );

  const setScore = (slug: string, value: number) =>
    setScores((prev) => ({ ...prev, [slug]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await onSave({ scores, comments, risks });
      if (updated) setIsComplete(updated.is_complete);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  const editable = canEdit && !locked;

  if (locked) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          bgcolor: mutedCard,
          borderRadius: "10px",
          p: 2,
        }}
      >
        <LockOutlinedIcon sx={{ fontSize: 18, color: textMuted }} />
        <Typography sx={{ fontSize: 13, color: textMuted }}>
          {lockedReason ?? "Locked until the previous stage is complete."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={1.5} sx={{ mb: 2.5 }}>
        {Object.entries(criteria).map(([slug, label]) => (
          <Box
            key={slug}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <Rating
              value={scores[slug] ?? null}
              precision={0.5}
              readOnly={!editable}
              onChange={(_, val) => val !== null && setScore(slug, val)}
              sx={{
                color: "#F79E1B",
                fontSize: 22,
                "& .MuiRating-iconEmpty": {
                  color: isDark ? `${textMuted}30` : "#E0E0E0",
                },
              }}
            />
            <Typography
              sx={{
                fontSize: 12.5,
                fontWeight: 600,
                color: textMuted,
                textAlign: "right",
              }}
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Stack>

      <Stack spacing={1.5} sx={{ mb: editable ? 2.5 : 0 }}>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: textMuted, mb: 0.5 }}>
            Comments
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            value={comments}
            disabled={!editable}
            onChange={(e) => setComments(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                bgcolor: mutedCard,
                fontSize: 12,
                color: textPrimary,
                "& fieldset": { borderColor: `${textMuted}20` },
                "&:hover fieldset": { borderColor: `${textMuted}40` },
                "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
              },
            }}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: textMuted, mb: 0.5 }}>
            Risks identified
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            value={risks}
            disabled={!editable}
            onChange={(e) => setRisks(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                bgcolor: mutedCard,
                fontSize: 12,
                color: textPrimary,
                "& fieldset": { borderColor: `${textMuted}20` },
                "&:hover fieldset": { borderColor: `${textMuted}40` },
                "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
              },
            }}
          />
        </Box>
      </Stack>

      {editable && (
        <Button
          variant="outlined"
          fullWidth
          onClick={handleSave}
          disabled={saving}
          startIcon={isComplete ? <DoneAllIcon /> : <SaveIcon />}
          sx={{
            textTransform: "none",
            borderRadius: "10px",
            py: 1,
            fontWeight: 700,
            fontSize: 13,
            borderColor: isComplete ? "#4BA663" : `${textMuted}55`,
            color: isComplete ? "#4BA663" : textPrimary,
            "&:hover": {
              borderColor: isComplete ? "#4BA663" : `${textMuted}88`,
            },
          }}
        >
          {saving
            ? "Saving…"
            : justSaved
              ? "Saved"
              : isComplete
                ? "Done"
                : "Save evaluation"}
        </Button>
      )}
    </Box>
  );
}
